import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Save } from "lucide-react";

const TTS_VOICES = ["alloy", "echo", "fable", "onyx", "nova", "shimmer", "coral", "verse", "ballad", "sage"];
const REALTIME_VOICES = ["alloy", "echo", "shimmer", "verse", "coral", "ballad", "sage"];
const VAD_LEVELS = [
  { value: 1, label: "1 — Patient", desc: "Waits ~1.2s of silence. Good for quiet rooms / slow speakers. Background noise less likely to interrupt." },
  { value: 2, label: "2 — Balanced (recommended)", desc: "Waits ~0.8s of silence. Best for most users in normal environments." },
  { value: 3, label: "3 — Snappy", desc: "Waits ~0.4s of silence. Good for noisy rooms / fast speakers. May cut you off mid-thought." },
];

export default function VoiceTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    api.get("/admin/voice-settings").then(({ data }) => {
      setSettings(data);
      setOriginal(data);
    }).catch(() => toast.error("Failed to load voice settings"));
  }, []);

  if (!settings) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const dirty = JSON.stringify(settings) !== JSON.stringify(original);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/voice-settings", settings);
      setSettings(data);
      setOriginal(data);
      toast.success("Voice settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const current = VAD_LEVELS.find((v) => v.value === settings.vad_aggressiveness) || VAD_LEVELS[1];

  return (
    <div className="space-y-6">
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">Voice configuration</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Controls how Nexa sounds and listens during voice calls.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
        <div className="bg-card p-6">
          <Label className="text-xs uppercase tracking-wider font-mono">TTS voice (decoupled / thinking-model mode)</Label>
          <Select value={settings.tts_voice} onValueChange={(v) => setSettings({ ...settings, tts_voice: v })}>
            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="voice-tts-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TTS_VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1.5 font-mono">
            Used by the "thinking model" voice path (OpenAI gpt-4o-mini-tts).
          </p>
        </div>

        <div className="bg-card p-6">
          <Label className="text-xs uppercase tracking-wider font-mono">Realtime voice (default fast mode)</Label>
          <Select value={settings.realtime_voice} onValueChange={(v) => setSettings({ ...settings, realtime_voice: v })}>
            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="voice-realtime-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REALTIME_VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1.5 font-mono">
            Used by the "Default — Nexa" voice path (gpt-realtime).
          </p>
        </div>

        <div className="bg-card p-6">
          <Label className="text-xs uppercase tracking-wider font-mono">Default language (ISO 639-1)</Label>
          <Input
            value={settings.default_language}
            onChange={(e) => setSettings({ ...settings, default_language: e.target.value.toLowerCase().slice(0, 2) })}
            placeholder="en"
            className="mt-1.5 h-10 rounded-sm font-mono w-24"
            data-testid="voice-lang-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1.5 font-mono">
            Pre-selected in the "Start call" modal. e.g. en, es, fr, hi.
          </p>
        </div>

        <div className="bg-card p-6">
          <Label className="text-xs uppercase tracking-wider font-mono">VAD aggressiveness</Label>
          <Select
            value={String(settings.vad_aggressiveness)}
            onValueChange={(v) => setSettings({ ...settings, vad_aggressiveness: Number(v) })}
          >
            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="voice-vad-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VAD_LEVELS.map((v) => <SelectItem key={v.value} value={String(v.value)}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-2 font-mono leading-relaxed">
            {current.desc}
          </p>
        </div>
      </div>

      {/* Voice-3 — RAG retrieval depth + transcript indexing */}
      <div className="border-t border-border pt-5">
        <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-3">Conversation RAG</div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">Retrieved chunks per voice turn</Label>
            <Input
              type="number"
              min="3"
              max="12"
              value={settings.voice_corpus_top_k ?? 6}
              onChange={(e) => setSettings({ ...settings, voice_corpus_top_k: parseInt(e.target.value, 10) || 6 })}
              className="mt-1.5 rounded-sm font-mono"
              data-testid="setting-voice-corpus-top-k"
            />
            <p className="text-[11px] text-muted-foreground mt-2 font-mono leading-relaxed">
              How many snippets of past chat + attached files Faro retrieves each time the user speaks. 3 = cheapest, 6 = balanced, 12 = richest context.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">Index voice transcripts into RAG</Label>
            <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!settings.voice_index_transcript_to_rag}
                onChange={(e) => setSettings({ ...settings, voice_index_transcript_to_rag: e.target.checked })}
                className="h-4 w-4"
                data-testid="setting-voice-index-transcripts"
              />
              <span>Push voice transcripts into Qdrant after each call</span>
            </label>
            <p className="text-[11px] text-muted-foreground mt-2 font-mono leading-relaxed">
              When ON, after a call ends the saved transcript is also embedded into Tensornex so future calls + chats in this conversation can retrieve from it. Adds a small Tensornex embedding cost per call.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={!dirty || saving} className="rounded-sm" data-testid="voice-save-button">
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : dirty ? "Save voice settings" : "Saved"}
        </Button>
        <span className="text-xs text-muted-foreground font-mono">
          Changes apply to the next voice call.
        </span>
      </div>
    </div>
  );
}
